// src/features/contactSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { AppDispatch, RootState } from "../app/store";
import { apiForSSO } from "../utils/utility";

type AsyncStatus = "idle" | "loading" | "succeeded" | "failed";

export interface SupportTicketPayload {
  name: string;
  email: string;
  subject: string;
  message: string;
  platform: string; // e.g. "boxchaos"
}

export interface SupportTicketResponse {
  detail: string; // "Ticket created successfully."
}

interface ContactState {
  createTicketStatus: AsyncStatus;
  createTicketError: string | null;
  isCreateTicketInProgress: boolean;
  lastTicketResponse: SupportTicketResponse | null;
}

const initialState: ContactState = {
  createTicketStatus: "idle",
  createTicketError: null,
  isCreateTicketInProgress: false,
  lastTicketResponse: null,
};

/**
 * POST /support/ticket[?user_id=...]
 * Body: { name, email, subject, message, platform }
 */
export const createSupportTicket = createAsyncThunk<
  SupportTicketResponse,
  { payload: SupportTicketPayload; userId?: string },
  { dispatch: AppDispatch; rejectValue: string }
>("contact/createSupportTicket", async ({ payload, userId }, { rejectWithValue }) => {
  try {
    const res = await apiForSSO.post("/support/ticket", payload, {
      params: userId ? { user_id: userId } : undefined,
      headers: { "Content-Type": "application/json" },
    });
    return res.data as SupportTicketResponse;
  } catch (error: any) {
    if (error?.response) {
      const status = error.response.status;
      const detail =
        error.response.data?.detail ||
        error.response.data?.message ||
        "Request failed";
      if (status === 400) return rejectWithValue(detail);
      if (status === 401) return rejectWithValue("Unauthorized. Please sign in again.");
      return rejectWithValue(`${status}: ${detail}`);
    }
    return rejectWithValue("Network error. Please try again.");
  }
});

const contactSlice = createSlice({
  name: "contact",
  initialState,
  reducers: {
    resetCreateTicketStatus(state) {
      state.createTicketStatus = "idle";
      state.createTicketError = null;
      state.isCreateTicketInProgress = false;
      state.lastTicketResponse = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSupportTicket.pending, (state) => {
        state.createTicketStatus = "loading";
        state.createTicketError = null;
        state.isCreateTicketInProgress = true;
      })
      .addCase(
        createSupportTicket.fulfilled,
        (state, action: PayloadAction<SupportTicketResponse>) => {
          state.createTicketStatus = "succeeded";
          state.isCreateTicketInProgress = false;
          state.lastTicketResponse = action.payload;
        }
      )
      .addCase(createSupportTicket.rejected, (state, action) => {
        state.createTicketStatus = "failed";
        state.createTicketError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to create support ticket";
        state.isCreateTicketInProgress = false;
      });
  },
});

export const { resetCreateTicketStatus } = contactSlice.actions;
export default contactSlice.reducer;


